<template>
  <div class="p-4 flex flex-col gap-4 min-w-80">
    <slot name="header"></slot>
    <div class="flex flex-col gap-4 md:max-h-50dvh md:overflow-auto p-[2px]">
      <div>
        <SelectInput
          :model-value="filters.departmentIds"
          label="By Department"
          :options="departmentOptions"
          multiselect
          @update:modelValue="(e) => updateFilter('departmentIds', e)"
        />
      </div>
      <div class="flex flex-col gap-2">
        <div class="font-medium">Appointment Status</div>
        <div class="rounded-lg border divide-y">
          <div
            v-for="status in statusOptions"
            :key="status.value"
            class="px-2 pt-2"
          >
            <Checkbox
              :model-value="isChecked(status.value)"
              @update:modelValue="(e) => updateStatusFilter(status, e)"
            >
              <StatusLabel :label="status.label" :bg-hex="status.color" />
            </Checkbox>
          </div>
        </div>
      </div>
      <div v-if="jobStatusEnabled">
        <SelectInput
          :model-value="filters.jobStatusIds"
          label="Job Status"
          :options="jobStatusOptions"
          multiselect
          @update:modelValue="(e) => updateFilter('jobStatusIds', e)"
        />
      </div>
      <div>
        <SelectInput
          :model-value="filters.serviceCategoryIds"
          label="Service Category"
          :options="serviceCategoryOptions"
          multiselect
          @update:modelValue="(e) => updateFilter('serviceCategoryIds', e)"
        />
      </div>
    </div>
    <div>
      <Button
        type="Secondary"
        label="Clear Filters"
        size="small"
        @click="clearFilters"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useStore } from "vuex";

import Button from "@/components/Seranova/Buttons/Button.vue";
import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import StatusLabel from "@/components/Seranova/Indicator/StatusLabel.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import CONSTANTS from "@/Constants";
import { useScheduleColors } from "@/SchedulerV2/useScheduleColors";
import { Department } from "@/typings/department";
const { APPOINTMENT_SCHEDULE_LEGEND } = CONSTANTS;

const { getStatusColor } = useScheduleColors();

const store = useStore();

const filters = computed(() => store.state.timeline.filters);

const departmentOptions = computed(() =>
  store.state.departments.map((dept: Department) => ({
    label: dept.name,
    value: dept.id
  }))
);

const jobStatusEnabled = computed(() =>
  store.getters.settingEnabledForTenant("job_status_enabled")
);
const jobStatusOptions = computed(() =>
  store.state.jobStatuses.map((status: { key: string; name: string }) => ({
    label: status.name,
    value: status.key
  }))
);

const serviceCategoryOptions = computed(() =>
  store.state.serviceCategories.map((sc: { id: number; name: string }) => ({
    label: sc.name,
    value: sc.id
  }))
);

const statusOptions = computed(() =>
  Object.entries(APPOINTMENT_SCHEDULE_LEGEND).map(
    ([status, { label, subLabel }]) => ({
      label: `${label}${subLabel ? ` ${subLabel}` : ""}`,
      value: status,
      color: getStatusColor(status)
    })
  )
);

onMounted(() => {
  store.dispatch("fetchJobStatuses");
  store.dispatch("fetchDepartments");
  store.dispatch("fetchServiceCategories");
});

const updateFilter = (key, value) => {
  store.dispatch("updateTimelineFilter", { key, value: value || [] });
};

const updateStatusFilter = (
  status: { label: string; value: string },
  checked: boolean
) => {
  const current = filters.value.statuses;
  const value = checked
    ? [...current, status.value]
    : current.filter((s) => s !== status.value);

  store.dispatch("updateTimelineFilter", { key: "statuses", value });
};

const clearFilters = () => store.dispatch("clearTimelineFilters");
const isChecked = (status: string) => filters.value.statuses.includes(status);
</script>

<style scoped></style>
